import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DwDesignerStateService, DwMetaDataGridComponent, DwSecurityUserService } from '@devwareapps/devware-cap';
import { Subscription } from 'rxjs';
import { CentralAdminAccessService } from './central-admin-access.service';
import { CentralAdminDataService } from './central-admin-data.service';
import { ChallengeContextService } from '../../shared/contexts/challenge-context/challenge-context.service';

export type CentralAdminTab = 'overview' | 'by-day' | 'registrations' | 'donations';

@Component({
    selector: 'app-central-admin', templateUrl: './central-admin.component.html',
    styleUrls: ['./central-admin.component.scss'], encapsulation: ViewEncapsulation.None,
    providers: [CentralAdminDataService],
})
export class CentralAdminComponent implements OnDestroy {
    readonly tabs: { key: CentralAdminTab; label: string }[] = [
        { key: 'overview', label: 'Overview' }, { key: 'by-day', label: 'By Day' },
        { key: 'registrations', label: 'Registrations' }, { key: 'donations', label: 'Donations' },
    ];
    tab: CentralAdminTab = 'overview';
    visited: Partial<Record<CentralAdminTab, boolean>> = { overview: true };
    scopes: { challengeId: number; revision: number }[] = [];
    private readonly _subscriptions = new Subscription();
    private _scopeId = 0;
    private _revision = 0;

    constructor(public access: CentralAdminAccessService, public data: CentralAdminDataService,
        private _route: ActivatedRoute, private _router: Router, selection: ChallengeContextService,
        security: DwSecurityUserService, private _designer: DwDesignerStateService) {
        this._subscriptions.add(_route.queryParamMap.subscribe(params => {
            const requested = params.get('adminView');
            this.tab = this.tabs.find(item => item.key === requested)?.key || 'overview';
            this.visited[this.tab] = true;
        }));
        this._subscriptions.add(selection.challengeChange$.subscribe(challenge => {
            if (challenge.ChallengeDefinitionId !== this._scopeId) {
                this._scopeId = challenge.ChallengeDefinitionId;
                this.resetViews();
            }
        }));
        this._subscriptions.add(security.securityContextChanged().subscribe(() => {
            this.resetViews();
            if (!access.allowed) { this._router.navigate(['/streaker-central']); }
        }));
    }

    select(tab: CentralAdminTab): void {
        this._router.navigate([], { relativeTo: this._route,
            queryParams: { adminView: tab === 'overview' ? null : tab }, queryParamsHandling: 'merge' });
    }

    configureGrid(grid: DwMetaDataGridComponent): void {
        if (!this.access.allowed || !grid.canConfigure || !grid.preferenceHierarchy) { return; }
        grid.startConfigSession();
        this._designer.configureSection(grid.selectedViewKey || 'default', undefined, grid.getConfigKey());
    }

    tabKey(event: KeyboardEvent, index: number): void {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) { return; }
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1
            : (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
        this.select(this.tabs[next].key);
        const buttons = (event.currentTarget as HTMLElement).parentElement.querySelectorAll<HTMLButtonElement>('[role="tab"]');
        buttons[next].focus();
    }

    trackScope(_index: number, scope: { revision: number }): number { return scope.revision; }

    private resetViews(): void {
        this.visited = { [this.tab]: true };
        // Recreate grids when the account or selected streak changes. Hide/show
        // alone preserves grids within a scope, including filters and paging.
        this.scopes = this.access.allowed && this._scopeId > 0
            ? [{ challengeId: this._scopeId, revision: ++this._revision }] : [];
    }

    ngOnDestroy(): void { this._subscriptions.unsubscribe(); }
}
